import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AdminFiltersService } from './admin-filters.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { environment } from '../../../../environments/environment';

/**
 * Edit / Add Filter Group modal.
 *
 * Migrated from AngularJS `saveFiltersController`
 * (`GTW12/ui/app/components/admin/adminFilters/adminFiltersController.js`).
 *
 * Layout matches the original `admin-filter-tpl.html` — three accordion sections
 * (Filter / Filter Group / Filter Map) with a 3-column field layout per filter row,
 * a red minus-circle delete button per row, an internal Save inside the Filter
 * section (saves filter list only) and a footer Save+Cancel (saves filter group +
 * filter map and closes the dialog).
 */
@Component({
    selector: 'gtw-admin-filter-edit',
    templateUrl: './admin-filter-edit.component.html',
    styleUrls: ['./admin-filter-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminFilterEditComponent implements OnInit, OnDestroy {
  loading = false;
  crudLoading = false;
  crudLoadingFilter = false;
  minimise = false;
  // Validation flag for the inner Filter section Save button.
  submitted = false;
  // Validation flag for the footer Save button (covers Filter Group + Filter Map).
  // Kept separate so user toggling between the two doesn't show stale errors.
  submittedGroup = false;
  title = 'Filter Group';
  mode: 'new' | 'edit' = 'new';

  filterSectionOpen = true;
  filterGroupSectionOpen = true;
  filterMapSectionOpen = true;

  filterGroup: any = {
    filter_group_key: '',
    filter_group_name: '',
    filter_group_type: '',
    filter_group_desc: '',
  };

  filtersList: any[] = [];
  filterMapList: any[] = [];

  uiType: any[] = [];
  allFiltersForMap: any[] = [];

  multipleOptions = [
    { name: 'single', value: 0 },
    { name: 'multiple', value: 1 },
  ];
  requiredOptions = [
    { name: 'No', value: 0 },
    { name: 'Yes', value: 1 },
  ];
  dataTypeOptions = [
    { name: 'static', value: 'static' },
    { name: 'load', value: 'load' },
    { name: 'inherit', value: 'inherit' },
    { name: 'text', value: 'text' },
  ];

  showConfirmDelete = false;
  pendingDelete: { type: 'filter' | 'filterMap'; key: any; index: number } | null = null;
  confirmDeleteConfig: ConfirmationModalConfig = {
    title: 'Delete Record',
    message: 'Are you sure you want to delete this record?',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  private rowData: any = {};
  // client_key actively selected in the parent filter bar (the dropdown next
  // to "Add New Filter"). When present this MUST take precedence over the
  // user's default client_key from USER_SETTINGS — otherwise the modal would
  // silently save the new filter under the wrong client. Mirrors the behaviour
  // the AngularJS app had via reactive USER_SETTINGS.user.client mutation.
  private selectedClientKey: any = undefined;
  private readonly baseUrl = environment.base_url;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<AdminFilterEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any,
    private adminFiltersService: AdminFiltersService,
    private appService: AppService,
    private globalService: GlobalService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.mode = data?.mode || (this.rowData?.FILTER_GROUP_KEY ? 'edit' : 'new');
    this.selectedClientKey = data?.selectedClientKey;
  }

  // Resolution order:
  //   1. client_key passed in via dialog data (the dropdown the user actually
  //      picked on the screen). Use `!= null` so 0 is treated as a real value.
  //   2. user's default from USER_SETTINGS.
  //   3. sessionStorage fallback.
  // Use ?? (not ||) for the fallbacks so that a legitimate client_key === 0
  // is preserved. See migration guide §6.10 Pitfall M1 and §9 Pitfall 9.
  private get clientKey(): number | string {
    if (this.selectedClientKey !== undefined && this.selectedClientKey !== null && this.selectedClientKey !== '') {
      return this.selectedClientKey;
    }
    return this.appService.USER_SETTINGS?.user?.client_key
      ?? this.appService.USER_SETTINGS?.user?.client?.client_key
      ?? sessionStorage.getItem('client_key')
      ?? '';
  }

  // Wraps GlobalService.global.globalParams for JsonObjectFactory.saveJSON6 — this
  // is what makes the request payload include jcd_key / issue_id / issue_short_desc
  // / checkLockedbj (added automatically by saveJSON6 from globalParams).
  // Mirrors template-assign.component.ts pattern.
  private get globalServiceObj(): any {
    return { globalParams: this.globalService.global?.globalParams || {} };
  }

  ngOnInit(): void {
    this.loadFiltersUIType();
    if (this.mode === 'edit') {
      const key = this.rowData?.FILTER_GROUP_KEY ?? this.rowData?.filter_group_key ?? '';
      this.filterGroup.filter_group_key = key;
      this.loadFilterByKey(key);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // CRUD — Filters list (in-memory, then bulk-saved via Save in Filter section)
  // ---------------------------------------------------------------------------

  // NOTE: We push (append) rather than unshift (prepend) on purpose.
  // Inputs in the row use [name]="'..._' + i" to register with the parent
  // NgForm. With unshift, every existing row's index shifts by +1 and the
  // new row at index 0 collides with an already-registered FormControl name,
  // which causes Angular to overwrite the existing control and visually
  // wipe out previously entered values. Push gives the new row a fresh,
  // never-used index — same pattern admin-action-edit uses (addSecondaryAction).
  addFilter(): void {
    this.filtersList.push({
      filter_key: '',
      param_name: '',
      label: '',
      ui_type: '',
      multiple: '',
      data_url: '',
      data_type: '',
      default_value: '',
      required: '',
      refresh_list: '',
      static_values: '',
      ui_type_other: '',
    });
    this.cdr.detectChanges();
  }

  addFilterMap(): void {
    this.filterMapList.push({
      filter_map_key: '',
      filter_key: '',
      filter_priority: '',
    });
    this.cdr.detectChanges();
  }

  clearFilter(): void {
    this.filtersList.forEach((item: any) => {
      item.param_name = '';
      item.multiple = '';
      item.required = '';
      item.label = '';
      item.data_url = '';
      item.refresh_list = '';
      item.ui_type = '';
      item.data_type = '';
      item.default_value = '';
      item.ui_type_other = '';
      item.static_values = '';
    });
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Validation helpers (mirror the original `saveFiltersController.is*Missing`)
  // ---------------------------------------------------------------------------

  private isMissing(value: any): boolean {
    return value === undefined || value === null || value === '';
  }

  isParamNameMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.param_name);
  }
  isLabelMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.label);
  }
  isUITypeMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.ui_type);
  }
  isUITypeOtherMissing(i: number): boolean {
    return this.submitted
      && this.filtersList[i]?.ui_type === 'Other'
      && this.isMissing(this.filtersList[i]?.ui_type_other);
  }
  isMultipleMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.multiple);
  }
  isDataUrlMissing(i: number): boolean {
    return this.submitted
      && this.filtersList[i]?.data_type === 'load'
      && this.isMissing(this.filtersList[i]?.data_url);
  }
  isDataTypeMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.data_type);
  }
  isRequiredMissing(i: number): boolean {
    return this.submitted && this.isMissing(this.filtersList[i]?.required);
  }
  isStaticJsonMissing(i: number): boolean {
    return this.submitted
      && this.filtersList[i]?.data_type === 'static'
      && this.isMissing(this.filtersList[i]?.static_values);
  }

  // Footer Save (saveFilterGroup) — Filter Group + Filter Map validation.
  // Original AngularJS `vm.save` had no validation at all — it accepted blank
  // submissions and the backend silently no-op'd them, which is what the user
  // hit. We add real validation here.
  isGroupNameMissing(): boolean {
    return this.submittedGroup && this.isMissing(this.filterGroup?.filter_group_name);
  }
  isGroupTypeMissing(): boolean {
    return this.submittedGroup && this.isMissing(this.filterGroup?.filter_group_type);
  }
  isMapFilterKeyMissing(i: number): boolean {
    return this.submittedGroup && this.isMissing(this.filterMapList[i]?.filter_key);
  }
  isMapPriorityMissing(i: number): boolean {
    return this.submittedGroup && this.isMissing(this.filterMapList[i]?.filter_priority);
  }

  // ---------------------------------------------------------------------------
  // Confirm delete (uses gtw-confirmation-modal — guide §6.9)
  // ---------------------------------------------------------------------------

  confirmDeleteFilter(filterKey: any, index: number): void {
    this.pendingDelete = { type: 'filter', key: filterKey, index };
    this.confirmDeleteConfig = {
      ...this.confirmDeleteConfig,
      message: filterKey
        ? 'Are you sure you want to Delete this record? This Filter key is used in multiple places, it will affect all the screens.'
        : 'Are you sure you want to remove this row?',
    };
    this.showConfirmDelete = true;
    this.cdr.detectChanges();
  }

  confirmDeleteFilterMap(filterMapKey: any, index: number): void {
    this.pendingDelete = { type: 'filterMap', key: filterMapKey, index };
    this.confirmDeleteConfig = {
      ...this.confirmDeleteConfig,
      message: 'Are you sure you want to permanently delete this record?',
    };
    this.showConfirmDelete = true;
    this.cdr.detectChanges();
  }

  onConfirmDeleteYes(): void {
    if (!this.pendingDelete) {
      this.showConfirmDelete = false;
      return;
    }
    const { type, key, index } = this.pendingDelete;
    if (type === 'filter') {
      this.deleteFilter(key, index);
    } else {
      this.deleteFilterMap(key, index);
    }
    this.pendingDelete = null;
    this.showConfirmDelete = false;
    this.cdr.detectChanges();
  }

  onConfirmDeleteNo(): void {
    this.pendingDelete = null;
    this.showConfirmDelete = false;
    this.cdr.detectChanges();
  }

  private deleteFilter(filterKey: any, index: number): void {
    if (!filterKey) {
      this.filtersList.splice(index, 1);
      this.cdr.detectChanges();
      return;
    }
    this.adminFiltersService.removeFilter(this.baseUrl, this.clientKey, filterKey, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Filter removed', 'gtw-snackbar--green');
            this.loadFiltersUIType();
            this.loadFilterByKey(this.filterGroup?.filter_group_key);
          } else {
            this.snackbar.openSnackBar('Unable to remove filter', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackbar.openSnackBar('Unable to remove filter', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private deleteFilterMap(filterMapKey: any, index: number): void {
    if (!filterMapKey) {
      this.filterMapList.splice(index, 1);
      this.cdr.detectChanges();
      return;
    }
    this.adminFiltersService.removeFilterMap(this.baseUrl, this.clientKey, filterMapKey, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Filter map removed', 'gtw-snackbar--green');
            this.loadFiltersUIType();
            this.loadFilterByKey(this.filterGroup?.filter_group_key);
          } else {
            this.snackbar.openSnackBar('Unable to remove filter map', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackbar.openSnackBar('Unable to remove filter map', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Save — Filters list (internal Save in Filter section)
  // ---------------------------------------------------------------------------

  saveFilters(): void {
    if (this.crudLoadingFilter) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.submitted = true;
    this.cdr.detectChanges();

    let invalid = false;
    this.filtersList.forEach((_item: any, i: number) => {
      if (this.isParamNameMissing(i)) invalid = true;
      if (this.isLabelMissing(i)) invalid = true;
      if (this.isUITypeMissing(i)) invalid = true;
      if (this.isUITypeOtherMissing(i)) invalid = true;
      if (this.isMultipleMissing(i)) invalid = true;
      if (this.isDataTypeMissing(i)) invalid = true;
      if (this.isRequiredMissing(i)) invalid = true;
      if (this.isDataUrlMissing(i)) invalid = true;
      if (this.isStaticJsonMissing(i)) invalid = true;
    });
    if (invalid) {
      return;
    }

    this.filtersList.forEach((item: any) => {
      if (item.ui_type === 'Other' || item.ui_type === 'Ohter') {
        item.ui_type = item.ui_type_other;
      }
      if (item.data_type === 'load') {
        item.static_values = '';
      }
    });

    this.crudLoadingFilter = true;
    this.cdr.detectChanges();

    this.adminFiltersService
      .saveUpdateFiltersByClient(this.baseUrl, this.clientKey, this.filtersList, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.crudLoadingFilter = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar(
              'Filters saved. Newly assigned filter keys are now visible above — you can map them in the Filter Map section below.',
              'gtw-snackbar--green',
            );
            // Refreshes allFiltersForMap dropdown AND merges new filter_keys
            // into local filtersList (see mergeAssignedFilterKeys).
            this.loadFiltersUIType();
          } else {
            this.snackbar.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoadingFilter = false;
          this.snackbar.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Save — Filter Group + Filter Map (footer Save) — closes dialog on success
  // ---------------------------------------------------------------------------

  saveFilterGroup(): void {
    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    // Mark form as submitted so isXxxMissing() helpers light up red borders +
    // error text. Reset on successful save.
    this.submittedGroup = true;
    this.cdr.detectChanges();

    // Required Filter Group fields.
    const groupErrors: string[] = [];
    if (this.isGroupNameMissing()) groupErrors.push('Filter Group Name');
    if (this.isGroupTypeMissing()) groupErrors.push('Filter Group Type');
    if (groupErrors.length > 0) {
      this.snackbar.openSnackBar(
        'Please fill in required fields: ' + groupErrors.join(', ') + '.',
        'gtw-snackbar--yellow',
      );
      return;
    }

    // Block when a filter row was added but never saved — its filter_key is
    // empty, which means it doesn't exist in the DB and can't be referenced
    // by filter_map. Users would lose this row on close.
    const hasUnsavedFilters = this.filtersList.some((f: any) => !f.filter_key);
    if (hasUnsavedFilters) {
      this.snackbar.openSnackBar(
        'You have unsaved filters in the Filter section. Please click Save there first.',
        'gtw-snackbar--yellow',
      );
      return;
    }

    // Block when nothing is mapped at all — original allowed this and backend
    // silently no-op'd, which looked like a successful save but produced no
    // record. A filter group without at least one mapping is meaningless.
    if (this.filterMapList.length === 0) {
      this.snackbar.openSnackBar(
        'No filter mappings defined. Add at least one Filter Map row before saving the group.',
        'gtw-snackbar--yellow',
      );
      return;
    }

    // Each Filter Map row needs both Filter Key and Filter Priority.
    const invalidMapRow = this.filterMapList.findIndex((_fm: any, i: number) =>
      this.isMapFilterKeyMissing(i) || this.isMapPriorityMissing(i)
    );
    if (invalidMapRow !== -1) {
      this.snackbar.openSnackBar(
        `Filter Map row ${invalidMapRow + 1}: Filter Key and Priority are required.`,
        'gtw-snackbar--yellow',
      );
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.adminFiltersService
      .saveFilterGroupByClient(this.baseUrl, this.clientKey, this.filterGroup, this.filterMapList, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.submittedGroup = false;
            this.snackbar.openSnackBar('Filter group saved successfully', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            this.snackbar.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  // ---------------------------------------------------------------------------
  // Data loaders
  // ---------------------------------------------------------------------------

  private loadFiltersUIType(): void {
    if (!this.baseUrl) return;
    this.adminFiltersService.getFiltersUIType(this.baseUrl, this.clientKey, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            const list: any[] = res?.FilterList || [];
            this.allFiltersForMap = list;
            const uniqueUiTypes = Array.from(
              new Set(list.map((item: any) => item?.ui_type).filter((v: any) => !!v))
            );
            this.uiType = uniqueUiTypes.map((v: any) => ({ name: v, value: v }));
            this.uiType.push({ name: 'Other', value: 'Other' });
            // After (re)loading the client-level filter list, propagate any newly
            // assigned filter_key values back into the local filtersList — so the
            // user can see them and reference them in the Filter Map dropdown.
            this.mergeAssignedFilterKeys(list);
          } else {
            this.allFiltersForMap = [];
            this.uiType = [{ name: 'Other', value: 'Other' }];
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.allFiltersForMap = [];
          this.uiType = [{ name: 'Other', value: 'Other' }];
          this.cdr.detectChanges();
        },
      });
  }

  // For each row in filtersList that does not yet have a filter_key (just saved
  // via Internal Save in new mode), find the matching client-level filter by
  // param_name and copy its filter_key. No-op when filtersList is empty or all
  // rows already carry a key.
  private mergeAssignedFilterKeys(allClientFilters: any[]): void {
    if (!this.filtersList?.length || !allClientFilters?.length) return;
    this.filtersList.forEach((row: any) => {
      if (!row.filter_key && row.param_name) {
        const match = allClientFilters.find((f: any) => f?.param_name === row.param_name);
        if (match?.filter_key) {
          row.filter_key = match.filter_key;
        }
      }
    });
  }

  private loadFilterByKey(filterGroupKey: any): void {
    if (!this.baseUrl || !filterGroupKey) return;
    this.loading = true;
    this.cdr.detectChanges();

    this.adminFiltersService
      .getFiltersByKey(this.baseUrl, this.clientKey, filterGroupKey, this.globalServiceObj, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res?.callSuccess === '1') {
            this.filtersList = res?.filterData || [];
            this.filterGroup = (res?.filterGroupData || [])[0] || this.filterGroup;
            this.filterMapList = res?.filterMapData || [];
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }
}
